import { ICashFloat, ICashTransfer } from "@/interfaces/cash-float/cash-float";
import CashFloatModule from "@/store/modules/cash-float";
import UserModule from "@/store/modules/user";
import DeviceModule from "@/store/modules/device";
import { isBelgianCompliant } from "./compliance";
import { isMultiCurrencyEnabled } from "@/utils/helpers/feature-flags";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { nowDB } from "../helpers/dates";
import { LogLevel, LogService } from "@/services/logging";
import { computed } from "vue";
import ConfirmLogout from "@/components/common/ConfirmLogout.vue";
import ConfirmClosingSales from "@/components/common/ConfirmClosingSales.vue";
import ModalModule from "@/store/modules/modal";
import { pushSyncCashTransfer } from "@/api/cash-float/cash-float";
import { hasNonEmptyOrders } from "./orders.helper";
import NotificationService from "@/services/notification.service";
import { getCashfloatByUidFromBackend } from "@/api/gql/cashfloat";
import { isCashbookV2Enabled } from "@/utils/helpers/feature-flags";

export async function openTopUpForm() {
  if (isCashbookV2Enabled()) {
    // Cashbook V2 opening flow — currently identical to legacy; will diverge
    // as subsequent PRs land the new opening UI behind this gate.
    CashFloatModule.setIsOpeningCashfloat(true);
    return;
  }
  CashFloatModule.setIsOpeningCashfloat(true);
}

// add current user to the userSwitch array if there's a cashfloat already open
export async function addToUserSwitch() {
  const cashFloat = CashFloatModule.cashFloat;
  if (!cashFloat) {
    return;
  }
  try {
    // confirm if cashfloat is still open and can be used by another user
    await checkCashfloatOnBackend(cashFloat);
  } catch (error) {
    LogService.info(
      `Error when checking cashfloat with uuid ${cashFloat.uid} on backend`,
      {
        error,
      },
      LogLevel.Error
    );
  }
  const userId = UserModule.userId;
  cashFloat.userSwitch.push({
    user_id: userId as number,
    datetime: nowDB(),
    //validAmounts: cashFloat.validAmounts || null,
    //amounts: cashFloat.amounts,
    //messages: cashFloat.messages || {},
  });
  LogService.info(
    `User is assigned to pre-existing cashfoat with uuid ${cashFloat.uid}`,
    {
      cashfloat: cashFloat,
      userId,
    }
  );
}

export async function checkCashfloatOnBackend(cashFloat: ICashFloat) {
  // check asynchronously if cashfloat exists on backend with closeDate set, raise an alert if so
  // This is in case the app wasn't closed properly and cashfloat was synced to backend but not closed on device
  const cf = await getCashfloatByUidFromBackend(cashFloat.uid);
  if (cf && cf.closeDate) {
    LogService.info(
      `ALERT: Cashfloat with uid ${cashFloat.uid} is closed on backend but open on device!`,
      {
        cashfloatBackend: cf,
        cashfloatDevice: cashFloat,
      }
    );
    CashFloatModule.setCashFloat(null);
  }
}

export const hasSalesStarted = computed(() => {
  // return false;
  return CashFloatModule.cashFloat;
});

/**
 * Confirm sign-out while a cash float is still open.
 *
 * @returns {Promise<boolean>} Resolves `true` if the user confirms sign-out.
 */
export async function confirmLogout(): Promise<boolean> {
  ModalModule.setShallLogout(true);
  return showConfirm({
    title: t("nav.sign-out"),
    body: ConfirmLogout,
    variant: hasNonEmptyOrders.value ? "destructive" : "default",
    confirmText: t("nav.sign-out"),
  });
}

/**
 * Confirm closing sales when there are incomplete or partially paid bookings.
 *
 * @returns {Promise<boolean>} Resolves `true` if the user confirms.
 */
export async function confirmClosingSales(): Promise<boolean> {
  return showConfirm({
    title: t("nav.close-sales"),
    body: ConfirmClosingSales,
    variant: "destructive",
    confirmText: t("button.confirm"),
    cancelText: t("button.back"),
  });
}

// Linked devices are single-currency unless the group has pos::multi-currency
export const hasMultiCurrenciesEnabled = computed(() => {
  // Belgian fiscal reporting assumes a single currency, whatever the flag says
  if (isBelgianCompliant.value) {
    return false;
  }
  if (isMultiCurrencyEnabled()) {
    return true;
  }
  return !DeviceModule.hardwareLocation?.deviceId;
});

/**
 * Synchronizes cash transfer, sends proper logs and inform user in case of failure
 * Returns true if the sync was successful, false otherwise.
 * @param cashTransfer
 */
export async function handleCashTransferSync(cashTransfer: ICashTransfer) {
  try {
    LogService.info(`Synching cash transfer with uuid: ${cashTransfer.uuid}`, {
      params: cashTransfer,
    });
    await pushSyncCashTransfer(cashTransfer);
    return true;
  } catch (err) {
    const isNetworkError = !err.response;
    const message = isNetworkError
      ? t("error.cash-transfer-network-error")
      : t("error.general-error");
    LogService.info(
      `Error when synching cash transfer with uuid: ${cashTransfer.uuid}`,
      {
        params: { error: err, cashTransfer: cashTransfer },
      }
    );
    NotificationService.showCustomMessageWithTitle(
      message,
      t("error.cash-transfer"),
      "error"
    );
    return false;
  }
}
